import { TenderCatalogSchema } from "schema/Tender/TenderCatalogSchema";
import { ATSTenderCatalog } from "transformer/tender/tenderCatalogTransformer";

// Valide et transforme les données de tarification des appels d'offres de l'api pour le store
export const validateAndTransformTenderCatalog = async (data) => {
  try {
    const catalog =  ATSTenderCatalog(data);
    const validData = await TenderCatalogSchema.validate(catalog, {
      abortEarly: false,
    });
    return validData;
  } catch (error) {
    console.log(error)
    throw error;
  }
};

/**
 * Retrouve l'item du catalogue AO correspondant à un produit / groupe / forfait.
 *
 * @param {object|null} tenderCatalog — `state.tender.tenderCatalog.data`
 * @param {{productId?: number, groupId?: number, packageId?: number}} ref
 * @returns {object|null}
 */
export const findTenderCatalogItem = (tenderCatalog, { productId, groupId, packageId } = {}) => {
  const items = tenderCatalog?.items;
  if (!items || !items.length) return null;
  if (productId != null) return items.find((i) => i.product_id === productId) || null;
  if (groupId != null) return items.find((i) => i.group_id === groupId) || null;
  if (packageId != null) return items.find((i) => i.package_id === packageId) || null;
  return null;
};

/**
 * Sélectionne le palier (pricing) applicable d'un item de catalogue AO.
 *
 * Reprend la règle déjà utilisée dans l'éditeur de devis :
 *  1. le palier qui encadre le nombre de personnes pour ce type de prestation ;
 *  2. sinon le palier valide (prix renseigné, min ≤ personnes) dont le `max`
 *     est le plus proche du nombre de personnes.
 *
 * @returns {object|null}
 */
export const findTenderCatalogPricing = (item, { person = 1, prestationType = null } = {}) => {
  const pricings = item?.pricings;
  if (!pricings || !pricings.length) return null;

  const matches = (pricing) =>
    pricing.price !== null &&
    pricing.price !== undefined &&
    Array.isArray(pricing.prestation_types) &&
    (prestationType === null || pricing.prestation_types.includes(prestationType));

  const direct = pricings.find(
    (pricing) => matches(pricing) && pricing.min_persons <= person && pricing.max_persons >= person
  );
  if (direct) return direct;

  const candidates = pricings.filter((pricing) => matches(pricing) && pricing.min_persons <= person);
  if (!candidates.length) return null;

  return candidates.reduce((closest, pricing) => {
    const currentDistance = Math.abs(pricing.max_persons - person);
    const closestDistance = Math.abs(closest.max_persons - person);
    return currentDistance < closestDistance ? pricing : closest;
  }, candidates[0]);
};

/**
 * Prix unitaire à appliquer pour un produit / groupe / forfait, en tenant
 * compte de l'appel d'offres sélectionné.
 *
 * Ordre de priorité : palier du catalogue AO → prix de l'item AO → prix
 * standard du catalogue Le Moulin (`fallbackPrice`).
 *
 * @param {object|null} tenderCatalog
 * @param {{productId?: number, groupId?: number, packageId?: number}} ref
 * @param {{person?: number, prestationType?: string|null, fallbackPrice?: number|null}} context
 * @returns {{price: number|null, renammed: string|null, fromTender: boolean}}
 */
export const resolveTenderUnitPrice = (
  tenderCatalog,
  ref = {},
  { person = 1, prestationType = null, fallbackPrice = null } = {}
) => {
  const item = findTenderCatalogItem(tenderCatalog, ref);
  if (!item) {
    return { price: fallbackPrice, renammed: null, fromTender: false };
  }

  const pricing = findTenderCatalogPricing(item, { person, prestationType });
  const tenderPrice = pricing?.price != null ? pricing.price : item.price;
  const hasTenderPrice = tenderPrice != null;

  return {
    price: hasTenderPrice ? tenderPrice : fallbackPrice,
    renammed: item.renammed || null,
    fromTender: hasTenderPrice,
  };
};
